import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import {
  privateTemplateCreateSchema,
  privateTemplateSchema,
  privateTemplateUpdateSchema,
} from "@rosc/contracts";
import { useMutation } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { referenceClient } from "@/lib/orpc/client";
import {
  getVisitScope,
  useVisitScope,
  type VisitScope,
} from "@/lib/visit-queries";

const orpc = createTanstackQueryUtils(referenceClient);
const deletedSchema = privateTemplateSchema.pick({ public_id: true });
type Outcome<T> =
  | { ok: true; value: T; scope: VisitScope }
  | { ok: false; scope: VisitScope; error: unknown; attempted: boolean };
export function useTemplateWrites() {
  const scope = useVisitScope();
  const context = { expectedUserUid: scope?.uid };
  const create = useMutation(
    orpc.createTemplate.mutationOptions({ context, retry: false }),
  );
  const update = useMutation(
    orpc.updateTemplate.mutationOptions({ context, retry: false }),
  );
  const remove = useMutation(
    orpc.deleteTemplate.mutationOptions({ context, retry: false }),
  );
  const [state, setState] = useState<{
    scope: VisitScope;
    phase: "pending" | "success" | "error";
  } | null>(null);
  const sequence = useRef(0),
    pending = useRef<VisitScope | null>(null);
  const run = async <T>(
    write: (markAttempted: () => void) => Promise<T>,
  ): Promise<Outcome<T> | null> => {
    if (!scope || getVisitScope() !== scope || pending.current === scope)
      return null;
    const attempt = ++sequence.current;
    pending.current = scope;
    const current = () =>
      getVisitScope() === scope && sequence.current === attempt;
    setState({ scope, phase: "pending" });
    let attempted = false;
    try {
      const value = await write(() => {
        attempted = true;
      });
      if (!current()) return null;
      setState({ scope, phase: "success" });
      return { ok: true, value, scope };
    } catch (error) {
      if (!current()) return null;
      setState({ scope, phase: "error" });
      return { ok: false, scope, error, attempted };
    } finally {
      if (sequence.current === attempt) pending.current = null;
    }
  };
  return {
    create: (body: unknown) =>
      run(async (markAttempted) => {
        const input = privateTemplateCreateSchema.parse(body);
        markAttempted();
        return privateTemplateSchema.parse(
          await create.mutateAsync({ body: input }),
        );
      }),
    update: (id: string, body: unknown) =>
      run(async (markAttempted) => {
        const target = privateTemplateSchema.shape.public_id.parse(id);
        const input = privateTemplateUpdateSchema.parse(body);
        markAttempted();
        const value = privateTemplateSchema.parse(
          await update.mutateAsync({
            params: { public_id: String(target) },
            body: input,
          }),
        );
        if (value.public_id !== target)
          throw Error("Unexpected template identity");
        return value;
      }),
    remove: (id: string) =>
      run(async (markAttempted) => {
        const target = privateTemplateSchema.shape.public_id.parse(id);
        markAttempted();
        const value = deletedSchema.parse(
          await remove.mutateAsync({ params: { public_id: String(target) } }),
        );
        if (value.public_id !== target)
          throw Error("Unexpected template identity");
        return value;
      }),
    loading: state?.scope === scope && state.phase === "pending",
    error: state?.scope === scope && state.phase === "error",
  };
}
